/**
 * 画面IDをもつページで共通して持つ親クラス
 *
 */
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AswCommonType, DynamicParams, LogType } from '@lib/interfaces';
import { CommonLibService, LoggerDatadogService, PageInitService } from '@lib/services';
import { AswCommonState } from '@lib/store';
import { Observable, of } from 'rxjs';
import { CheckinBaseComponent } from './checkin-base-component';
import { Title } from '@angular/platform-browser';
import { StaticMsgPipe } from '@lib/pipes';
import { BrowserBackService } from '../../services/browser-back/browser-back.service';

/**
 * ページで共通して持つ親クラス
 */
@Component({
  template: '',
})
export abstract class CheckinBasePageComponent extends CheckinBaseComponent implements OnInit, OnDestroy {
  constructor(private ___common: CommonLibService, private _pageInitService: PageInitService) {
    super(___common);
    this._loggerDatadogService = inject(LoggerDatadogService);
    this._staticMsgPipe = inject(StaticMsgPipe);
    this._titleService = inject(Title);
    this._browserBackService = inject(BrowserBackService);
  }

  /** 画面ID */
  abstract pageId: string;
  /** 機能ID */
  abstract functionId: string;
  /** 画面タイトルの静的文言キー */
  abstract pageTitleKey: string;
  /** 動的文言判定用パラメータ取得用Observable */
  public params: Observable<DynamicParams> = of({});
  /**
   * 画面の初期化終了処理を基底クラス側で完了するかどうかのフラグ
   * 画面の初期化処理を基底クラス側で完了する場合はtrue、ページ独自のタイミングで初期化終了処理を呼ぶ場合はfalse
   */
  public isAutoInitEnd: boolean = true;
  /** 画面表示の際のログ出力を行うためのサービス*/
  private _loggerDatadogService;
  /** 静的文言表示用パイプ */
  private _staticMsgPipe;
  /** タイトルを変更するためのサービス */
  private _titleService;
  /** ブラウザバックを検知するためのサービス */
  private _browserBackService;

  override ngOnInit(): void {
    // 画面の初期化処理(G01-003)開始
    this._pageInitService.startInit();

    // 画面タイトルの更新
    const title = `${this._staticMsgPipe.transform(this.pageTitleKey)}${this._staticMsgPipe.transform(
      'm_static_message-label.aswPageTitle'
    )}`;
    this._titleService.setTitle(title);

    // 機能ID、画面ID、チェックインフラグの更新
    const updateValue: Partial<AswCommonState> = {
      [AswCommonType.PAGE_ID]: this.pageId,
      [AswCommonType.FUNCTION_ID]: this.functionId,
      // 提携：提携では不要のためコメントアウト
      // [AswCommonType.IS_CHECKIN]: true,
    };
    this.___common.aswCommonStoreService.updateAswCommon(updateValue);

    // PageViewEventログの出力
    this._loggerDatadogService.info(LogType.PAGE_VIEW, {});

    // 継承元のngOnInitを呼び出す(継承元の中で継承先で定義されているinitメソッドを呼び出す)
    super.ngOnInit();

    // 画面の処理化修了処理をテンプレート側で行う場合は、initの最後で呼び出す。
    if (this.isAutoInitEnd) {
      this._pageInitService.endInit(this.params);
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    //注意喚起エリアをクリア
    this.___common.errorsHandlerService.clearRetryableError('page');
    this.___common.alertMessageStoreService.removeAllAlertMessage();
    this.clearDynamicMessageId();

    // 画面の初期化処理にて使用したSubscribeの解放
    this._pageInitService.pageInitUnsubscribe();
  }

  override _reload() {
    super._reload();
  }

  public clearDynamicMessageId() {
    this.___common.dynamicContentService.clearDynamicContent('page');
  }

  /**
   * ブラウザバック(フォワード)を検知して処理を行うためのメソッド
   * @param onBrowserBack ブラウザバック(フォワード)実行時に実施される関数
   */
  public setupProcessOnBrowserBack(onBrowserBack: () => void) {
    const watchBrowserBack$ = this._browserBackService.watchBrowserBack();
    this.subscribeService(watchBrowserBack$, () => {
      onBrowserBack();
    });
  }
}
